import React from 'react';
import { cn } from '../../../utils/cn';
import { ProgressBar } from '../feedback/ProgressBar';

/**
 * MetricCard - KPI metric card component
 *
 * A polished card component for displaying key performance indicators with optional
 * progress bars, trend indicators, and colored icons. Designed for dashboard views.
 *
 * @example
 * ```tsx
 * <MetricCard
 *   title="Requested"
 *   value="48"
 *   subtitle="Team Weeks"
 *   icon={<ChartBarIcon />}
 *   iconColor="blue"
 *   progress={87.5}
 *   trend={{ value: 12, direction: 'up' }}
 * />
 * ```
 */

export interface MetricCardProps {
  /** Metric title label */
  title: string;
  /** Primary metric value (number or formatted string) */
  value: string | number;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Icon element to display */
  icon: React.ReactNode;
  /** Icon background color variant */
  iconColor: 'blue' | 'violet' | 'emerald' | 'amber' | 'cyan' | 'rose';
  /** Progress percentage (0-100), shows progress bar if provided */
  progress?: number;
  /** Trend indicator with value and direction */
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  /** Additional CSS classes */
  className?: string;
}

const iconColorMap = {
  blue: 'bg-accent-blue-soft text-accent-blue dark:bg-accent-blue-soft dark:text-accent-blue',
  violet: 'bg-accent-violet-soft text-accent-violet dark:bg-accent-violet-soft dark:text-accent-violet',
  emerald: 'bg-accent-emerald-soft text-accent-emerald dark:bg-accent-emerald-soft dark:text-accent-emerald',
  amber: 'bg-accent-amber-soft text-accent-amber dark:bg-accent-amber-soft dark:text-accent-amber',
  cyan: 'bg-accent-cyan-soft text-accent-cyan dark:bg-accent-cyan-soft dark:text-accent-cyan',
  rose: 'bg-accent-rose-soft text-accent-rose dark:bg-accent-rose-soft dark:text-accent-rose',
};

const trendColorMap = {
  up: 'text-status-success',
  down: 'text-status-error',
};

/**
 * MetricCard Component
 *
 * Features:
 * - Glass morphism card with hover-lift effect
 * - Colored icon backgrounds with soft colors
 * - Large metric values with tabular numbers
 * - Optional progress bar visualization
 * - Optional trend indicators
 * - Responsive layout
 */
export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  iconColor,
  progress,
  trend,
  className,
}) => {
  return (
    <div className={cn('glass rounded-xl p-5 hover-lift', className)}>
      {/* Header: Title and Icon */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wide">
          {title}
        </span>
        <div
          className={cn(
            'w-8 h-8 rounded-lg flex items-center justify-center',
            iconColorMap[iconColor]
          )}
        >
          {icon}
        </div>
      </div>

      {/* Metric Value */}
      <div className="flex items-baseline gap-2 overflow-hidden">
        <p className="text-3xl font-bold text-foreground metric-value">{value}</p>
        {trend && (
          <span
            className={cn(
              'text-sm font-semibold flex items-center gap-0.5',
              trendColorMap[trend.direction]
            )}
          >
            {trend.direction === 'up' ? '↑' : '↓'}
            {Number.isFinite(trend.value) ? trend.value.toFixed(1) : trend.value}%
          </span>
        )}
      </div>

      {/* Subtitle */}
      {subtitle && <p className="text-sm text-muted-foreground/60 mt-1">{subtitle}</p>}

      {/* Progress Bar */}
      {progress !== undefined && (
        <div className="mt-3">
          <ProgressBar
            value={progress}
            color={iconColor}
            size="sm"
            showShimmer
            gradient
          />
        </div>
      )}
    </div>
  );
};

MetricCard.displayName = 'MetricCard';
